// Server dictionaries (GET /dictionary/<namespace>) for every app: the localStorage cache, the fetch
// of one or more dictionaries and the lookup with {{token}} placeholders. No store, no React: the
// webapp keeps its Redux store, the other apps use useDictionaryLabels.
//
// The cache is per origin (each subdomain has its own): same key and shape as the webapp,
// labels_<area>_<locale> -> { labels, lastModified }.

export const cacheKeyOf = (area, locale) => `labels_${area}_${locale}`;

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

// null when missing, broken or the storage is not usable (private window, blocked site data).
export const readCachedLabels = (storage, key) => {
  try {
    const parsed = JSON.parse(storage.getItem(key));
    return isPlainObject(parsed) && isPlainObject(parsed.labels)
      ? { labels: parsed.labels, lastModified: parsed.lastModified ?? null }
      : null;
  } catch {
    return null;
  }
};

// A full or blocked storage only loses the cache.
export const writeCachedLabels = (storage, key, { labels, lastModified }) => {
  try {
    storage.setItem(key, JSON.stringify({ labels, lastModified }));
  } catch {
    // nothing to do: the labels are still shown, only not kept
  }
};

// The server texts replace the cached ones when there is no cache, no server date (unknown: take
// them) or a later server date.
export const isNewer = (lastModified, cached) => {
  if (!cached || !lastModified || !cached.lastModified) return true;
  return Date.parse(lastModified) > Date.parse(cached.lastModified);
};

const lastModifiedOf = (response) => {
  const time = Date.parse(response?.headers?.["last-modified"]);
  return Number.isFinite(time) ? time : null;
};

const NOT_MODIFIED = 304;
const acceptStatus = (status) => (status >= 200 && status < 300) || status === NOT_MODIFIED;

// One GET per dictionary in the given language, merged in the order given (later wins), with the
// latest Last-Modified. complete is false when one failed or did not answer an object; labels is
// null when none did. With since (the date of the cache) the server answers 304 when not newer:
// all 304 is unchanged; some changed reads them all again without since, so the labels are whole.
export const fetchDictionaries = async ({ api, urls, locale, signal, since }) => {
  const results = await Promise.allSettled(
    urls.map((url) =>
      api.get(url, {
        headers: { "Accept-Language": locale },
        signal,
        ...(since && { params: { since }, validateStatus: acceptStatus }),
      })
    )
  );
  const notModified = results.filter(
    (result) => result.status === "fulfilled" && result.value?.status === NOT_MODIFIED
  ).length;
  if (since && notModified === results.length) {
    return { labels: null, lastModified: null, complete: true, unchanged: true };
  }
  if (since && notModified > 0) return fetchDictionaries({ api, urls, locale, signal });

  let labels = null;
  let latest = null;
  let complete = true;
  results.forEach((result) => {
    const data = result.status === "fulfilled" ? result.value?.data : null;
    if (!isPlainObject(data)) {
      complete = false;
      return;
    }
    labels = { ...(labels ?? {}), ...data };
    const time = lastModifiedOf(result.value);
    if (time !== null && (latest === null || time > latest)) latest = time;
  });
  return {
    labels,
    lastModified: latest === null ? null : new Date(latest).toISOString(),
    complete,
    unchanged: false,
  };
};

const replaceTokens = (text, tokens) =>
  Object.entries(tokens).reduce(
    (result, [token, value]) => result.replace(new RegExp(`\\{\\{${token}\\}\\}`, "g"), value ?? ""),
    text
  );

// getLabel(key, tokens) over the sources in order (the first one with the key wins), then
// missing(key, tokens), the key itself by default like the components without a provider. Values
// that are not strings (the footer links) come back as they are.
export const createGetLabel =
  (sources, missing = (key) => key) =>
  (key, tokens = {}) => {
    const values = tokens || {};
    const source = sources.find((labels) => labels && labels[key] !== undefined && labels[key] !== null);
    if (!source) return missing(key, values);
    const label = source[key];
    return typeof label === "string" ? replaceTokens(label, values) : label;
  };
